/**
 * Disabled-user helpers.
 *
 * Users have no "disabled" flag: disableDashboardUser deletes every ENABLED role item they
 * have, and the backend's isDashboardEnabled rule treats that item as the account's on/off
 * switch. So "has an ENABLED role item" is the only signal that an account is still active.
 */

/** Add to the AND array of a users() where clause to exclude disabled users server-side. */
export const ENABLED_USER_FILTER = {
  roles_some: { roleItems_some: { feature: 'ENABLED' } },
};

/** Did the selection set actually deliver roleItems for at least one role? */
const hasRoleItemData = (user) => {
  const roles = user && user.roles;
  return Array.isArray(roles) && roles.some((role) => Array.isArray(role && role.roleItems));
};

const hasEnabledRoleItem = (user) =>
  ((user && user.roles) || []).some((role) =>
    (role.roleItems || []).some((item) => item && item.feature === 'ENABLED')
  );

/**
 * Client-side counterpart, for user lists that come off a relation (team members, project
 * owner/team) instead of the users() query. Requires `roles { roleItems { feature } }` in the
 * selection set; fails open when that data is absent, so a user is only ever hidden when we can
 * positively prove they are disabled.
 */
export const isEnabledUser = (user) => (hasRoleItemData(user) ? hasEnabledRoleItem(user) : true);

/**
 * Auth-gate variant of isEnabledUser — fails CLOSED.
 *
 * "No positively-observed ENABLED role item" means "no session", including when roles or
 * roleItems are missing entirely. That is the opposite default from isEnabledUser, and the
 * difference is deliberate: at a list-filtering site a missing selection set should never hide
 * a real person, but at an auth gate the very states that make the data absent (roles: [],
 * roles: null, roleItems stripped) are exactly the ones that would re-open the hole this
 * closes.
 *
 * Only safe on selection sets that ALWAYS request roles { roleItems { feature } }.
 * AUTHENTICATED_USER does, unpaginated — see the note in that document.
 *
 * Read feature directly rather than going through getRolesFeatures(): that helper keeps only
 * items whose actions include READ, so an ENABLED item carrying just WRITE would vanish and a
 * live account would be locked out.
 */
export const isEnabledUserStrict = (user) => hasRoleItemData(user) && hasEnabledRoleItem(user);

export const filterEnabledUsers = (users) => (users || []).filter(isEnabledUser);
